<template>
    <v-dialog :value="value" max-width="640" persistent @input="$emit('input', $event)">
        <v-card class="ui-card">
            <div class="ui-card__header">
                <div>
                    <h3 class="ui-section-title">{{ meeting ? "Editar agenda" : "Nova agenda" }}</h3>
                    <p class="ui-section-description">Uma agenda pode cobrir vários casos do mesmo juiz. Os casos vão para "agenda marcada" e a agenda aparece na linha do tempo de cada um.</p>
                </div>
            </div>
            <div class="ui-card__body">
                <v-autocomplete v-model="form.case_ids" :items="caseItems" item-value="id" item-text="label" :search-input.sync="caseSearch" :loading="searching"
                    label="Casos" multiple chips small-chips deletable-chips outlined dense hide-details="auto" class="mb-3" no-filter
                    no-data-text="Digite o nome da empresa ou o processo"></v-autocomplete>
                <v-row dense>
                    <v-col cols="12" sm="5"><v-text-field v-model="form.date" type="date" label="Data" outlined dense hide-details="auto"></v-text-field></v-col>
                    <v-col cols="6" sm="3"><v-text-field v-model="form.time" type="time" label="Horário" outlined dense hide-details="auto"></v-text-field></v-col>
                    <v-col cols="6" sm="4"><v-select v-model="form.mode" :items="modeItems" label="Modalidade" outlined dense hide-details="auto" :menu-props="{ offsetY: true }"></v-select></v-col>
                    <v-col cols="12"><v-select v-model="form.attendee_ids" :items="teamItems" label="Responsáveis" multiple chips small-chips outlined dense hide-details="auto" :menu-props="{ offsetY: true }"></v-select></v-col>
                    <v-col cols="12" sm="7"><v-text-field v-model="form.with_whom" label="Com quem (juiz, assessor)" outlined dense hide-details="auto"></v-text-field></v-col>
                    <v-col cols="12" sm="5"><v-select v-model="form.onepage_status" :items="onepageItems" label="Onepage" outlined dense hide-details="auto" :menu-props="{ offsetY: true }"></v-select></v-col>
                    <v-col cols="12"><v-text-field v-model="form.location" :label="form.mode === 'presencial' ? 'Local (fórum, gabinete)' : 'Link ou observação de acesso'" outlined dense hide-details="auto"></v-text-field></v-col>
                    <v-col cols="12"><v-textarea v-model="form.notes" label="Observações (roteiro, material, pedidos)" outlined dense rows="2" auto-grow hide-details="auto"></v-textarea></v-col>
                </v-row>
            </div>
            <div class="ui-card__footer">
                <v-btn outlined :disabled="saving" @click="$emit('input', false)">Cancelar</v-btn>
                <v-btn v-if="$allowed('save')" color="primary" depressed :loading="saving" :disabled="!canSave" @click="save">{{ meeting ? "Salvar" : "Marcar agenda" }}</v-btn>
            </div>
        </v-card>
    </v-dialog>
</template>

<script>
import axios from "axios";
import { MEETING_MODE_ITEMS, ONEPAGE_ITEMS, notifyError, notifySuccess, todayKey } from "@/components/prospect/prospect.util";

function caseLabel(item) {
    return `${item.company_name} · ${item.type === "tca" ? "TCA" : "RJ"} · ${item.uf || "—"}${item.city ? ` · ${item.city}` : ""}`;
}

// Nova agenda ou edicao. Emite "saved" com a agenda gravada.
export default {
    name: "ProspectMeetingDialog",

    props: {
        value: { type: Boolean, default: false },
        meeting: { type: Object, default: null },
        // Casos pre-selecionados (ex.: a partir da ficha): [{ id, company_name, type, uf, city }]
        initialCases: { type: Array, default: () => [] },
    },

    data: () => ({
        form: {},
        team: [],
        caseOptions: [],
        caseSearch: "",
        searching: false,
        saving: false,
        timer: null,
        modeItems: MEETING_MODE_ITEMS,
        onepageItems: ONEPAGE_ITEMS,
    }),

    computed: {
        teamItems() {
            return this.team.map((person) => ({ value: person.id, text: person.name }));
        },
        caseItems() {
            return this.caseOptions.map((item) => ({ id: item.id, label: caseLabel(item) }));
        },
        canSave() {
            return this.form.case_ids && this.form.case_ids.length && this.form.date && this.form.time && this.form.mode && this.form.attendee_ids && this.form.attendee_ids.length;
        },
    },

    watch: {
        value: {
            immediate: true,
            handler(open) {
                if (open) this.reset();
            },
        },
        caseSearch(term) {
            clearTimeout(this.timer);
            if (!term || term.length < 2) return;
            this.timer = setTimeout(() => this.searchCases(term), 300);
        },
    },

    methods: {
        async reset() {
            const meeting = this.meeting;
            const cases = meeting ? meeting.cases : this.initialCases;
            this.caseOptions = [...cases];
            this.form = meeting ? {
                case_ids: meeting.cases.map((item) => item.id),
                date: meeting.date,
                time: meeting.time,
                mode: meeting.mode,
                attendee_ids: meeting.attendees.map((item) => item.id),
                with_whom: meeting.with_whom || "",
                onepage_status: meeting.onepage_status,
                location: meeting.location || "",
                notes: meeting.notes || "",
            } : {
                case_ids: cases.map((item) => item.id),
                date: todayKey(),
                time: "14:00",
                mode: "virtual",
                attendee_ids: [],
                with_whom: "",
                onepage_status: "none",
                location: "",
                notes: "",
            };
            if (!this.team.length) {
                try {
                    const result = await axios.get("/api/admin/prospect/team");
                    this.team = result.data.content;
                } catch (error) {
                    notifyError(this, error);
                }
            }
        },

        async searchCases(term) {
            this.searching = true;
            try {
                const result = await axios.get("/api/admin/prospect/cases", { params: { search: term, perPage: 20 } });
                const selected = this.caseOptions.filter((item) => this.form.case_ids.includes(item.id));
                const found = result.data.content.data.filter((item) => !selected.some((entry) => entry.id === item.id));
                this.caseOptions = [...selected, ...found];
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.searching = false;
            }
        },

        async save() {
            this.saving = true;
            try {
                const form = this.form;
                const payload = {
                    scheduled_at: `${form.date} ${form.time}`,
                    mode: form.mode,
                    case_ids: form.case_ids,
                    attendee_ids: form.attendee_ids,
                    with_whom: form.with_whom,
                    onepage_status: form.onepage_status,
                    location: form.location,
                    notes: form.notes,
                };
                const result = this.meeting
                    ? await axios.patch(`/api/admin/prospect/meetings/${this.meeting.id}`, payload)
                    : await axios.post("/api/admin/prospect/meetings", payload);
                notifySuccess(this, this.meeting ? "Agenda atualizada." : "Agenda marcada.");
                this.$emit("saved", result.data.content);
                this.$emit("input", false);
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>
